import { environment } from '../../environments/environment';
import { hasReferenceOrderId, normalizeEmail, resolveUserEmail, round2 } from './item-utils';
import {
  LineMarginViolation,
  MarginApprovalSummary,
  MarginValidationService,
  OrderMarginViolation
} from '../services/margin-validation.service';

export type MarginApprovalAction = 'save' | 'picklist' | 'sync';

type MarginEnv = {
  marginApprovalDivisions?: string;
  marginApprovalEmails_100_101?: string;
  marginApprovalEmails_300?: string;
  marginApprovalEmails_400?: string;
  marginBypassContact_100_101?: string;
  marginBypassContact_300?: string;
  marginBypassContact_400?: string;
  marginBypassContact_100_default?: string;
};

function parseEmailList(raw: string | undefined): string[] {
  return String(raw ?? '')
    .split(',')
    .map((e) => normalizeEmail(e))
    .filter(Boolean);
}

export function isMarginApprovalEnabledForDivision(divisionId: string): boolean {
  const env = environment as MarginEnv;
  const allowed = (env.marginApprovalDivisions || '')
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);
  const division = String(divisionId ?? '').trim();
  return division !== '' && allowed.includes(division);
}

function resolveApproverEmailConfigKey(division: string, warehouse: string): keyof MarginEnv | null {
  const d = String(division ?? '').trim();
  if (d === '300') {
    return 'marginApprovalEmails_300';
  }
  if (d === '400') {
    return 'marginApprovalEmails_400';
  }
  if (d === '100') {
    return 'marginApprovalEmails_100_101';
  }
  return null;
}

/** Resolve warehouse id for margin approval (order row, item, or session). */
export function resolveMarginWarehouse(sources: {
  warehouseId?: unknown;
  whsId?: unknown;
  warehouse?: unknown;
  storageWarehouseId?: unknown;
}): string {
  const candidates = [
    sources.warehouseId,
    sources.whsId,
    sources.warehouse,
    sources.storageWarehouseId
  ];
  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (value) {
      return value;
    }
  }
  return '';
}

export function resolveMarginApprovalEmails(division: string, warehouse: string): string[] {
  const env = environment as MarginEnv;
  const key = resolveApproverEmailConfigKey(division, warehouse);
  if (!key) {
    return [];
  }
  return parseEmailList(env[key] as string | undefined);
}

export function isMarginApprovalUser(
  userEmail: string | null | undefined,
  division: string,
  warehouse: string
): boolean {
  if (!isMarginApprovalEnabledForDivision(division)) {
    return false;
  }
  const normalized = normalizeEmail(userEmail ?? '');
  if (!normalized) {
    return false;
  }
  return resolveMarginAssistanceContactEmails(division, warehouse).includes(normalized);
}

function resolveContactRaw(division: string, warehouse: string): string {
  const env = environment as MarginEnv;
  const d = String(division ?? '').trim();
  const wh = String(warehouse ?? '').trim();
  if (d === '300') {
    return String(env.marginBypassContact_300 ?? '').trim();
  }
  if (d === '400') {
    return String(env.marginBypassContact_400 ?? '').trim();
  }
  if (d === '100' && wh === '101') {
    return String(env.marginBypassContact_100_101 ?? '').trim();
  }
  if (d === '100') {
    return String(env.marginBypassContact_100_default ?? env.marginBypassContact_100_101 ?? '').trim();
  }
  return '';
}

export function getMarginBypassContactNote(division: string, warehouse: string, action: MarginApprovalAction): string {
  const contact = resolveContactRaw(division, warehouse);
  if (!contact) {
    return 'Please contact your manager for margin approval.';
  }
  if (action === 'save') {
    return `For approval, contact ${contact}.`;
  }
  if (action === 'picklist') {
    return `For approval, contact ${contact}.`;
  }
  return `For approval, contact ${contact}.`;
}

function parseEmailsFromContactRaw(raw: string): string[] {
  const matches = String(raw ?? '').match(/[\w.+-]+@[\w.-]+\.\w+/g);
  if (!matches?.length) {
    return [];
  }
  return [...new Set(matches.map((e) => normalizeEmail(e)).filter(Boolean))];
}

/** All configured approver emails for assistance list (approver dialog). */
export function resolveMarginAssistanceContactEmails(division: string, warehouse: string): string[] {
  const fromConfig = resolveMarginApprovalEmails(division, warehouse);
  if (fromConfig.length) {
    return fromConfig;
  }
  return parseEmailsFromContactRaw(resolveContactRaw(division, warehouse));
}

/** Approver contacts shown to non-approvers (excludes logged-in user when they are an approver). */
export function resolveMarginBlockedContactEmails(
  division: string,
  warehouse: string,
  currentUserEmail?: string | null
): string[] {
  const all = resolveMarginAssistanceContactEmails(division, warehouse);
  const current = normalizeEmail(currentUserEmail ?? '');
  if (!current) {
    return all;
  }
  const filtered = all.filter((email) => email !== current);
  return filtered.length ? filtered : all;
}

export function getMarginApproverProceedNote(proceedActionLabel = 'Approve & Save Draft'): string {
  return `By selecting ${proceedActionLabel}, you approve this margin exception.`;
}

/** True when order already has a stored margin approval audit. */
export function isMarginApprovedOnOrder(source: {
  marginApproved?: unknown;
} | null | undefined): boolean {
  const v = source?.marginApproved;
  return v === true || v === 1 || v === '1' || String(v ?? '').trim().toLowerCase() === 'true';
}

/** Skip margin gate when already synced to M3 or margin already approved. */
export function shouldSkipMarginProcessingGate(
  referenceOrderId: unknown,
  source?: { marginApproved?: unknown; referenceOrderId?: unknown } | null
): boolean {
  if (hasReferenceOrderId(referenceOrderId ?? source?.referenceOrderId)) {
    return true;
  }
  return isMarginApprovedOnOrder(source);
}

export const MARGIN_GENERIC_WARNING_MESSAGE =
  'The contribution margin is below the minimum required threshold. Please review the pricing, quantity, rate, or discount and make the necessary adjustments before proceeding.';

export const MARGIN_ITEM_WARNING_MESSAGE =
  'The contribution margin is below the minimum required threshold. Please review and adjust the pricing, quantity, rate, or discount before proceeding.';

export const MARGIN_ITEM_WARNING_NOTE =
  'If no changes are made, the margin will remain below the minimum threshold and this may require approval to proceed.';

export const MARGIN_BLOCKED_THRESHOLD_MESSAGE = MARGIN_GENERIC_WARNING_MESSAGE;

export const MARGIN_BLOCKED_APPROVAL_NOTICE =
  'Approval is required before saving or submitting this order.';

export const MARGIN_BLOCKED_SAVE_NOTICE =
  'You may save this order as a draft. Further processing requires approval from the contacts above.';

export const MARGIN_BLOCKED_PROCEED_NOTICE =
  'Approval is required before proceeding. Contact the approvers above to continue.';

export function getMarginBlockedApprovalNotice(action: MarginApprovalAction): string {
  if (action === 'save') {
    return MARGIN_BLOCKED_SAVE_NOTICE;
  }
  return MARGIN_BLOCKED_PROCEED_NOTICE;
}

export const MARGIN_APPROVAL_THRESHOLD_MESSAGE = MARGIN_GENERIC_WARNING_MESSAGE;

export function resolveOrderMinimumMarginPercent(sources: {
  orderMcm?: unknown;
  minimumOrderMargin?: unknown;
  F1N096?: unknown;
}): number {
  return Number(sources.orderMcm ?? sources.minimumOrderMargin ?? sources.F1N096) || 0;
}

export function buildMarginApprovalSummary(
  marginService: MarginValidationService,
  itemList: any[],
  orderMinimumPercent: number
): MarginApprovalSummary {
  const failingLines = marginService.collectLineMarginViolations(itemList);
  const orderViolation =
    orderMinimumPercent > 0
      ? marginService.collectOrderMarginViolation(itemList, orderMinimumPercent)
      : null;
  const hasOrderViolation = orderViolation != null && !orderViolation.isValid;
  return {
    failingLines,
    orderViolation: hasOrderViolation ? orderViolation : null,
    hasAnyViolation: failingLines.length > 0 || hasOrderViolation
  };
}

export function syncLineMarginFlags(
  marginService: MarginValidationService,
  itemList: any[]
): void {
  (itemList || []).forEach((line) => {
    const result = marginService.validateRowContributionMargin(line);
    if (!result) {
      line.marginBelowMinimum = false;
      return;
    }
    line.calculatedMargin = result.actualMarginPercent;
    line.minimumMargin = result.minimumMarginPercent;
    line.marginBelowMinimum = !result.isValid;
    if (result.isValid) {
      line.lineCost = marginService.resolveLineCost(line);
    }
  });
}

export function countLinesBelowMinimumMargin(itemList: any[]): number {
  return (itemList || []).filter((line) => line?.marginBelowMinimum === true).length;
}

export type OrderMarginExceptionKind = 'line' | 'order' | 'both';

type OrderMarginProductSnapshot = {
  marginBelowMinimum?: boolean | null;
  marginOrderBelowMinimum?: boolean | null;
  marginViolationLineCount?: number | null;
  orderMcm?: number | null;
  minimumOrderMargin?: number | null;
  orderCost?: number | null;
  netAmount?: number | string | null;
  orderValue?: number | string | null;
};

/** Order list: order-header contribution margin below minimum (from header snapshot). */
export function calculateOrderHeaderMarginPercent(product: OrderMarginProductSnapshot): number | null {
  const selling = Number(product.netAmount ?? product.orderValue) || 0;
  const cost = Number(product.orderCost) || 0;
  if (selling <= 0 || cost <= 0) {
    return null;
  }
  return round2(((selling - cost) / selling) * 100);
}

/** Order list: order-header contribution margin below minimum (from header snapshot). */
export function resolveOrderHeaderMarginBelowMinimum(product: OrderMarginProductSnapshot): boolean {
  if (product.marginOrderBelowMinimum === true) {
    return true;
  }
  const required = resolveOrderMinimumMarginPercent({
    orderMcm: product.orderMcm,
    minimumOrderMargin: product.minimumOrderMargin
  });
  if (required <= 0) {
    return false;
  }
  const actual = calculateOrderHeaderMarginPercent(product);
  if (actual === null) {
    return false;
  }
  return actual < required;
}

export function resolveOrderLineMarginViolationCount(product: OrderMarginProductSnapshot): number {
  return Number(product.marginViolationLineCount) || 0;
}

export function resolveOrderMarginExceptionKind(
  product: OrderMarginProductSnapshot
): OrderMarginExceptionKind | null {
  const hasLineViolation = resolveOrderLineMarginViolationCount(product) > 0;
  const hasOrderViolation = resolveOrderHeaderMarginBelowMinimum(product);
  if (hasLineViolation && hasOrderViolation) {
    return 'both';
  }
  if (hasOrderViolation) {
    return 'order';
  }
  if (hasLineViolation) {
    return 'line';
  }
  return null;
}

export function getOrderMarginExceptionTooltipMessage(kind: OrderMarginExceptionKind): string {
  switch (kind) {
    case 'both':
      return 'Order and line margins below threshold.';
    case 'order':
      return 'Order margin below threshold.';
    case 'line':
      return 'One or more line margins below threshold.';
  }
}

export function buildOrderMarginExceptionTooltipHtml(product: OrderMarginProductSnapshot): string {
  const kind = resolveOrderMarginExceptionKind(product);
  if (!kind) {
    return '<div class="margin-exception-tooltip"><div class="margin-exception-tooltip__title">Margin Exception</div></div>';
  }
  const message = getOrderMarginExceptionTooltipMessage(kind);
  return `<div class="margin-exception-tooltip"><div class="margin-exception-tooltip__title">Margin Exception</div><div class="margin-exception-tooltip__item">${message}</div></div>`;
}

/** Order list: derive order-level margin violation from header snapshots (order_mcm, order_cost, net_amount). */
export function resolveProductMarginBelowMinimum(product: OrderMarginProductSnapshot): boolean {
  if (product.marginBelowMinimum === true) {
    return true;
  }
  if (resolveOrderLineMarginViolationCount(product) > 0) {
    return true;
  }
  return resolveOrderHeaderMarginBelowMinimum(product);
}

/** Map margin warning snapshot fields from GetOrderHeaders API item. */
export function mapHeaderMarginFieldsFromApi(item: any): {
  marginViolationLineCount: number;
  marginOrderBelowMinimum: boolean;
  marginBelowMinimum: boolean;
} {
  const orderMcm = item?.orderMcm != null && item?.orderMcm !== '' ? Number(item.orderMcm) : undefined;
  const orderCost = item?.orderCost != null && item?.orderCost !== '' ? Number(item.orderCost) : undefined;
  const minimumOrderMargin =
    item?.minimumOrderMargin != null && item?.minimumOrderMargin !== ''
      ? Number(item.minimumOrderMargin)
      : undefined;

  const snapshot: OrderMarginProductSnapshot = {
    orderMcm,
    orderCost,
    minimumOrderMargin,
    netAmount: item?.netAmount,
    orderValue: item?.orderValue,
    marginOrderBelowMinimum: item?.marginOrderBelowMinimum === true,
    marginViolationLineCount:
      item?.marginViolationLineCount != null && item?.marginViolationLineCount !== ''
        ? Number(item.marginViolationLineCount)
        : undefined,
    marginBelowMinimum: item?.marginBelowMinimum === true
  };

  const marginViolationLineCount =
    item?.marginViolationLineCount != null && item?.marginViolationLineCount !== ''
      ? Number(item.marginViolationLineCount) || 0
      : resolveOrderLineMarginViolationCount(snapshot);
  const marginOrderBelowMinimum =
    item?.marginOrderBelowMinimum === true || resolveOrderHeaderMarginBelowMinimum(snapshot);
  const marginBelowMinimum =
    item?.marginBelowMinimum === true || marginViolationLineCount > 0 || marginOrderBelowMinimum;

  return { marginViolationLineCount, marginOrderBelowMinimum, marginBelowMinimum };
}

export function isMarginApprovalUserFromStorage(
  storage: { get: (key: string) => unknown } | null | undefined,
  division: string,
  warehouse: string
): boolean {
  return isMarginApprovalUser(resolveUserEmail(storage), division, warehouse);
}

function canViewMarginColumns(
  division: string,
  storage?: { get: (key: string) => unknown } | null,
  warehouse?: string
): boolean {
  const d = String(division ?? '').trim();
  // Div 100 (DE) and 400 (NL): Margin/MCM columns visible to all users.
  if (d === '100' || d === '400') {
    return true;
  }
  return isMarginApprovalUserFromStorage(storage, division, warehouse ?? '');
}

export function showLineMarginColumns(
  division: string,
  isCreditNoteOrder: boolean,
  storage?: { get: (key: string) => unknown } | null,
  warehouse?: string
): boolean {
  if (!isMarginApprovalEnabledForDivision(division) || isCreditNoteOrder) {
    return false;
  }
  return canViewMarginColumns(division, storage, warehouse);
}

export function showOrderMarginColumns(
  division: string,
  storage?: { get: (key: string) => unknown } | null,
  warehouse?: string
): boolean {
  if (!isMarginApprovalEnabledForDivision(division)) {
    return false;
  }
  return canViewMarginColumns(division, storage, warehouse);
}

export function formatMarginPercent(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '—';
  }
  return `${round2(n).toFixed(1)}%`;
}

export function buildLineMarginTooltip(
  row: any,
  marginService: MarginValidationService
): string {
  const net = marginService.resolveLineSellingAmount(row);
  const lineCost = marginService.resolveLineCost(row);
  const actual = Number(row?.calculatedMargin);
  const required = Number(row?.minimumMargin ?? row?.itemMcm);
  const actualText = Number.isFinite(actual) ? `${round2(actual).toFixed(1)}%` : '—';
  const requiredText = Number.isFinite(required) && required > 0 ? `${round2(required).toFixed(1)}%` : '—';
  return [
    `Net after discount: ${round2(net).toFixed(2)}`,
    `Line cost: ${round2(lineCost).toFixed(2)}`,
    `Actual margin: ${actualText}`,
    `Required (MCM): ${requiredText}`
  ].join('\n');
}

export function buildOrderHeaderMarginTooltip(product: OrderMarginProductSnapshot): string {
  const net = Number(product.netAmount ?? product.orderValue) || 0;
  const cost = Number(product.orderCost) || 0;
  const actual = calculateOrderHeaderMarginPercent(product);
  const required = resolveOrderMinimumMarginPercent({
    orderMcm: product.orderMcm,
    minimumOrderMargin: product.minimumOrderMargin
  });
  return [
    `Net amount: ${round2(net).toFixed(2)}`,
    `Order cost: ${round2(cost).toFixed(2)}`,
    `Margin: ${formatMarginPercent(actual)}`,
    `MCM: ${formatMarginPercent(required)}`
  ].join('\n');
}
